import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, Statistic, Button, Space, Table, Tabs, Tag, Typography, message, Tooltip, theme } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useCredits } from '../../hooks/use_credits';
import { useNodeInfo } from '../../hooks/use_node_info';
import { useApi } from '../../hooks/use_api';

const { Text } = Typography;

const KIND_COLORS = { purchase: 'green', grant: 'blue', usage: 'default', adjustment: 'orange' };

function fmtWhen(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function fmtDuration(seconds) {
    if (seconds == null) return 'in progress';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m ? `${m} min ${s ? `${s} s` : ''}`.trim() : `${s} s`;
}

function roomLabel(row) {
    if (row.room_kind === 'entity') return `Hangout · entity ${row.room_ref}`;
    if (row.room_kind === 'gathering') return row.room_name.includes('-common-') ? 'Gathering · common room' : 'Gathering · breakout room';
    return row.room_name;
}

function ledgerNote(row) {
    const md = row.metadata || {};
    if (row.kind === 'usage') return `${roomLabel({ room_kind: md.room_kind, room_ref: md.room_ref, room_name: md.room_name || '' })} · ${fmtDuration(md.duration_seconds)}`;
    if (row.kind === 'purchase') return md.amount_total != null && md.currency
        ? `Paid ${(md.amount_total / 100).toFixed(2)} ${String(md.currency).toUpperCase()}`
        : 'Credit pack';
    return md.note || (row.kind === 'grant' ? 'Granted by an admin' : 'Adjusted by an admin');
}

/**
 * The signed-in user's credits (config.py feature_credits): balance, the packs
 * this node sells, and the history behind the number. Credits are per user and
 * spent per participant-minute in video rooms; v1 only meters and shows, so a
 * negative balance is possible and is shown as such. Renders nothing on a node
 * with credits off, so it is safe to mount unconditionally in Account.jsx.
 */
function Credits() {
    const api = useApi();
    const { token } = theme.useToken();
    const { featureCredits, creditsPerParticipantMinute } = useNodeInfo();
    const { balance, packs, recent, enforce, perMinute, enabled, loading, refresh, buy } = useCredits();
    const [searchParams, setSearchParams] = useSearchParams();
    const [buying, setBuying] = useState(null);
    const [usage, setUsage] = useState(null);
    const [tab, setTab] = useState('history');

    // Back from Stripe: the webhook that credits the ledger lags the redirect by
    // a second or two, so re-fetch a few times before giving up quietly.
    useEffect(() => {
        if (searchParams.get('credits') !== 'purchased' || !enabled) return;
        message.success('Thanks — your credits will appear in a moment.');
        const next = new URLSearchParams(searchParams);
        next.delete('credits');
        setSearchParams(next, { replace: true });
        const timers = [2000, 4000, 7000].map(ms => setTimeout(refresh, ms));
        return () => timers.forEach(clearTimeout);
    }, [searchParams, enabled, refresh, setSearchParams]);

    useEffect(() => {
        if (tab !== 'usage' || !enabled) return;
        api.get('/api/credits/me/usage', { params: { limit: 50 } })
            .then(rows => setUsage(rows || []))
            .catch(() => setUsage([]));
    }, [tab, enabled, api, balance]);

    if (!featureCredits || !enabled) return null;

    const handleBuy = async (priceId) => {
        setBuying(priceId);
        try {
            await buy(priceId);
        } catch (err) {
            message.error(err?.message || 'Checkout failed');
            setBuying(null);
        }
    };

    const rate = perMinute ?? creditsPerParticipantMinute;
    const low = balance <= 0;

    const historyColumns = [
        { title: 'When', dataIndex: 'created_at', width: 170, render: fmtWhen },
        { title: 'Kind', dataIndex: 'kind', width: 110, render: k => <Tag color={KIND_COLORS[k]}>{k}</Tag> },
        { title: 'Credits', dataIndex: 'delta', width: 90, align: 'right',
          render: d => <Text type={d < 0 ? 'secondary' : undefined}>{d > 0 ? `+${d}` : d}</Text> },
        { title: 'Note', key: 'note', render: (_, row) => <Text type="secondary">{ledgerNote(row)}</Text> },
    ];
    const usageColumns = [
        { title: 'Joined', dataIndex: 'joined_at', width: 170, render: fmtWhen },
        { title: 'Room', key: 'room', render: (_, row) => roomLabel(row) },
        { title: 'Duration', dataIndex: 'duration_seconds', width: 120, render: fmtDuration },
        { title: 'Credits', dataIndex: 'credits_charged', width: 90, align: 'right',
          render: c => (c == null ? '—' : c) },
    ];

    return (
        <Card
            title="Credits"
            style={{ marginTop: 16 }}
            extra={
                <Tooltip title="Refresh">
                    <Button type="text" size="small" icon={<ReloadOutlined />} loading={loading} onClick={refresh} />
                </Tooltip>
            }
        >
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Space size={32} wrap align="start">
                    <Statistic
                        title="Balance"
                        value={balance}
                        suffix="credits"
                        valueStyle={low ? { color: token.colorWarning } : undefined}
                    />
                    <div style={{ maxWidth: 420 }}>
                        <Text type="secondary">
                            Video calls cost {rate} {rate === 1 ? 'credit' : 'credits'} per minute you spend in a room,
                            counted per started minute.
                            {enforce
                                ? ' You need a positive balance to join a call.'
                                : ' Right now your balance is only tracked — you can still join calls when it runs out.'}
                        </Text>
                    </div>
                </Space>

                {packs.length > 0 && (
                    <Space wrap>
                        {packs.map(p => (
                            <Button
                                key={p.price_id}
                                type={low ? 'primary' : 'default'}
                                loading={buying === p.price_id}
                                disabled={buying != null && buying !== p.price_id}
                                onClick={() => handleBuy(p.price_id)}
                            >
                                Buy {p.label}
                            </Button>
                        ))}
                    </Space>
                )}

                <Tabs
                    size="small"
                    activeKey={tab}
                    onChange={setTab}
                    items={[
                        {
                            key: 'history',
                            label: 'History',
                            children: (
                                <Table
                                    size="small"
                                    rowKey="id"
                                    columns={historyColumns}
                                    dataSource={recent}
                                    pagination={false}
                                    locale={{ emptyText: 'No credits bought or spent yet' }}
                                />
                            ),
                        },
                        {
                            key: 'usage',
                            label: 'Calls',
                            children: (
                                <Table
                                    size="small"
                                    rowKey="id"
                                    columns={usageColumns}
                                    dataSource={usage || []}
                                    loading={usage === null}
                                    pagination={{ pageSize: 10, hideOnSinglePage: true }}
                                    locale={{ emptyText: 'No calls recorded yet' }}
                                />
                            ),
                        },
                    ]}
                />
            </Space>
        </Card>
    );
}

export default Credits;
